import { FC, useEffect, useState } from 'react'
import { Button } from 'views/components/UI/Buttons'
import Input from 'views/components/UI/TextInput'
import { Card } from './styled'
import {
  disconnectMicrosoftSso,
  getMicrosoftSso,
  saveMicrosoftSso,
  startMicrosoftSso,
} from 'services/companies/microsoftSso'
import styled from 'styled-components'

const SsoCard = styled(Card)`
  background: #ffffff;
  border: 1px solid #e4ebf3;
  box-shadow: 0 12px 28px rgba(23, 55, 77, 0.08);
  border-radius: 18px;
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: auto;
  min-height: 210px;
  margin: 28px 0 0;
  padding: 24px 28px;
  cursor: default;

  &:hover {
    transform: none;
    border-color: #e4ebf3;
    box-shadow: 0 12px 28px rgba(23, 55, 77, 0.08);
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
`

const Title = styled.div`
  h3 {
    margin: 0 0 4px;
    font-size: 20px;
    color: #172b4d;
  }
  p {
    margin: 0;
    color: #718096;
    font-size: 14px;
  }
`

const Status = styled.span<{ tone: 'success' | 'warning' | 'neutral' }>`
  padding: 7px 12px;
  border-radius: 16px;
  background: ${(props) => (props.tone === 'success' ? '#e8f7ef' : props.tone === 'warning' ? '#fff4df' : '#eef2f6')};
  color: ${(props) => (props.tone === 'success' ? '#18794e' : props.tone === 'warning' ? '#9a6700' : '#526274')};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`

const Fields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`

const Activation = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 18px;
  color: #172b4d;
  font-size: 14px;
  font-weight: 600;
  input {
    width: 18px;
    height: 18px;
    accent-color: #c50070;
  }
`

const Feedback = styled.p<{ error?: boolean }>`
  margin: 14px 0 0;
  color: ${(props) => (props.error ? '#b42318' : '#18794e')};
  font-size: 14px;
`

const MicrosoftSsoSection: FC<{ companyId: number }> = ({ companyId }) => {
  const [tenantId, setTenantId] = useState('')
  const [domains, setDomains] = useState('')
  const [connected, setConnected] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving'>('idle')
  const [feedback, setFeedback] = useState<{ message: string; error?: boolean; persistent?: boolean } | null>(null)
  const hasConfiguration = Boolean(tenantId.trim() && domains.trim())
  const status = connected
    ? enabled
      ? { message: 'SSO activo', tone: 'success' as const }
      : { message: 'Conectado: SSO desactivado', tone: 'warning' as const }
    : hasConfiguration
    ? { message: 'SSO desconectado', tone: 'neutral' as const }
    : { message: 'SSO sin configurar', tone: 'neutral' as const }

  useEffect(() => {
    getMicrosoftSso(companyId)
      .then((config) => {
        setTenantId(config.microsoft_tenant_id || '')
        setDomains(config.domains.join('\n'))
        setConnected(config.microsoft_sso_connected)
        setEnabled(config.microsoft_sso_enabled)
      })
      .catch(() =>
        setFeedback({
          message:
            'No fue posible cargar la configuración de SSO. Comprueba que tienes permisos para consultar esta empresa y vuelve a intentarlo.',
          error: true,
          persistent: true,
        })
      )
  }, [companyId])

  useEffect(() => {
    if (!feedback || feedback.persistent) return undefined
    const timeout = window.setTimeout(() => setFeedback(null), 4000)
    return () => window.clearTimeout(timeout)
  }, [feedback])

  const save = async () => {
    setSaveStatus('saving')
    try {
      const config = await saveMicrosoftSso(
        companyId,
        tenantId.trim(),
        domains
          .split(/[\n,]/)
          .map((domain) => domain.trim().toLowerCase())
          .filter(Boolean),
        enabled
      )
      setConnected(config.microsoft_sso_connected)
      setEnabled(config.microsoft_sso_enabled)
      setFeedback({ message: 'La configuración de SSO se guardó correctamente.' })
    } catch {
      setFeedback({ message: 'No fue posible guardar la configuración de SSO.', error: true })
    } finally {
      setSaveStatus('idle')
    }
  }

  const connect = async () => {
    try {
      window.location.assign(await startMicrosoftSso(companyId))
    } catch {
      setFeedback({ message: 'No fue posible iniciar la conexión con Microsoft.', error: true })
    }
  }

  const disconnect = async () => {
    try {
      const config = await disconnectMicrosoftSso(companyId)
      setConnected(config.microsoft_sso_connected)
      setEnabled(config.microsoft_sso_enabled)
      setFeedback({ message: 'Microsoft SSO se desconectó correctamente.' })
    } catch {
      setFeedback({ message: 'No fue posible desconectar Microsoft SSO.', error: true })
    }
  }

  return (
    <SsoCard>
      <Header>
        <Title>
          <h3>Microsoft SSO</h3>
          <p>Conecta esta empresa con su directorio Microsoft Entra.</p>
        </Title>
        <Status tone={status.tone}>{status.message}</Status>
      </Header>
      <Fields>
        <Input
          type="text"
          label="Tenant ID de Microsoft Entra"
          value={tenantId}
          onChange={(event) => setTenantId(event.target.value)}
          placeholder="Ej.: 00000000-0000-0000-0000-000000000000"
        />
        <Input
          type="textarea"
          label="Dominios corporativos"
          value={domains}
          onChange={(event) => setDomains(event.target.value)}
          placeholder="Uno por línea"
          minRows={2}
          maxRows={3}
        />
      </Fields>
      <Activation>
        <input
          type="checkbox"
          checked={enabled}
          disabled={!connected}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        Activar inicio de sesión con SSO
      </Activation>
      <Actions>
        <Button onClick={save} disabled={saveStatus === 'saving'}>
          {saveStatus === 'saving' ? 'Guardando configuración…' : 'Guardar configuración'}
        </Button>
        <Button onClick={connected ? disconnect : connect}>
          {connected ? 'Desconectar Microsoft' : 'Conectar con Microsoft'}
        </Button>
      </Actions>
      {feedback && <Feedback error={feedback.error}>{feedback.message}</Feedback>}
    </SsoCard>
  )
}
export default MicrosoftSsoSection
